import React from 'react';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import { NavLink } from 'react-router-dom';
import { displayAlert } from '../../../../slices/feedbackSlice';
import SocialMediaButtons from '../../../Layout/SharingButtons/SharingButtons';

const useStyles = makeStyles()((theme) => ({

  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  wrapper: {
    position: 'relative',
  },
  buttonProgress: {
    color: theme.palette.secondary.main,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  sharingButton: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  copyButton: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  embedField: {
    marginTop: theme.spacing(1),
  },
  embedInput: {
    fontFamily: 'monospace',
    // ⛔ Pas de taille réduite, même sur du code à copier : le plancher de
    // 16 px du thème vaut ici aussi. C'est un champ de saisie, et Safari sur
    // iOS zoome tout seul en dessous de cette taille.
    fontSize: '1rem',
  },
  embedActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
  },
}));

export default function ShareModal({ display, handleClose, sharingLink }) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('myBooks');

  const isSubscribed = useSelector((state) => state.organization.organization.is_subscribed);

  const handleCopy = () => {
    navigator.clipboard.writeText(sharingLink);
    dispatch(displayAlert(t('share_modal.link_copied'), 'success'));
  };

  // Le code d'intégration : la vitrine publique est embarquable telle quelle,
  // aucun en-tête ne l'interdit. Ce champ évite d'avoir à écrire la balise soi-même,
  // ce qui est tout l'intérêt pour un site tenu par une personne bénévole.
  const embedCode = `<iframe src="${sharingLink}" width="100%" height="900" `
    + 'style="border:0" loading="lazy" title="Notre collection"></iframe>';

  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    dispatch(displayAlert(t('share_modal.embed_copied'), 'success'));
  };

  const modalContent = (
    isSubscribed
      ? (
    // Subscribed
        <>
          <DialogTitle className={classes.root}>
            <Typography type="h3" variant="h3">
              {t('share_modal.title')}
            </Typography>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={handleClose}
              size="large"
            >
              <CloseIcon />
            </IconButton>

          </DialogTitle>

          <DialogContent dividers>
            <Typography type="p" variant="body2">
              {t('share_modal.embed_intro')}
            </Typography>
            <TextField
              className={classes.embedField}
              InputProps={{ readOnly: true, classes: { input: classes.embedInput } }}
              inputProps={{ 'aria-label': 'code d\'intégration' }}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={embedCode}
              onFocus={(e) => e.target.select()}
            />
            <div className={classes.embedActions}>
              <Button variant="outlined" color="primary" onClick={handleCopyEmbed}>
                {t('share_modal.copy_embed')}
              </Button>
            </div>
          </DialogContent>

          <DialogActions>
            <Grid container alignItems="flex-end" justifyContent="flex-end" direction="row">
              <Grid item xs={6} className={classes.sharingButton}>
                <SocialMediaButtons
                  url={sharingLink}
                  quote={t('sharing.quote')}
                  hashtags={t('sharing.hashtags').split()}
                />
              </Grid>
              <Grid item xs={6} className={classes.copyButton}>
                <Button
                  variant="contained"
                  component="label"
                  color="primary"
                  onClick={handleCopy}
                >
                  {t('share_modal.copy_link')}
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </>
      )
      : (

    // Not subscribed
        <>
          <DialogTitle className={classes.root}>
            <Typography type="h3" variant="h3">
              {t('share_modal.title')}
            </Typography>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={handleClose}
              size="large"
            >
              <CloseIcon />
            </IconButton>

          </DialogTitle>
          <DialogContent dividers>
            <Typography type="p" variant="body1">
              {t('share_modal.no_subscription')}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Grid container alignItems="flex-end" justifyContent="flex-end" direction="row">
              <Grid item xs={6} className={classes.copyButton}>
                <Button
                  variant="contained"
                  color="primary"
                  component={NavLink}
                  to="myaccount"
                >
                  {t('share_modal.subscribe')}
                </Button>
              </Grid>
            </Grid>

          </DialogActions>
        </>
      )
  );

  return (
    <Dialog
      open={display}
      onClose={handleClose}
      fullWidth
    >
      {modalContent}
    </Dialog>
  );
}
